// LOCATION: admin-portal/src/pages/UserManagement.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase'; 
import { 
  collection, onSnapshot, doc, updateDoc, Timestamp, getDocs, 
  query, where, collectionGroup, writeBatch, serverTimestamp 
} from 'firebase/firestore';
import * as XLSX from 'xlsx'; 
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './UserManagement.css'; 

const getCountryFromPhone = (phone) => {
  if (!phone || phone === 'No Phone') return 'Unknown';
  const cleanPhone = phone.replace(/[^+\d]/g, ''); 
  if (cleanPhone.startsWith('+1')) return 'USA/Canada';
  if (cleanPhone.startsWith('+44')) return 'UK';
  if (cleanPhone.startsWith('+91')) return 'India';
  if (cleanPhone.startsWith('+234')) return 'Nigeria';
  if (cleanPhone.startsWith('+27')) return 'South Africa';
  return 'Other';
};

const REASON_OPTIONS = [
  "Outstanding payment",
  "Suspicious Activity",
  "Terms of Service Violation",
  "Account Under Review",
  "Spamming / Harassment",
  "Admin Override",
  "Requested by User"
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [activityMap, setActivityMap] = useState({}); 
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [activityFilter, setActivityFilter] = useState('All');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [disableModal, setDisableModal] = useState({ isOpen: false, user: null, reason: '' });

  const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  const BUSINESS_CATEGORIES = {
    "1": { name: "Transport", fee: 5000 },
    "2": { name: "School", fee: 30000 },
    "3": { name: "Hospital", fee: 30000 },
    "4": { name: "Estate", fee: 50000 }
  };

  const fetchActivity = async () => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const threshold = Timestamp.fromDate(oneMonthAgo);
    const tempMap = {};
    try {
      const journeySnap = await getDocs(query(collection(db, 'journeys'), where('updatedAt', '>=', threshold)));
      journeySnap.forEach(doc => {
        const uid = doc.id;
        const date = doc.data().updatedAt?.toDate();
        if (date && (!tempMap[uid] || date > tempMap[uid])) tempMap[uid] = date;
      });
      const triggerSnap = await getDocs(query(collectionGroup(db, 'triggers'), where('createdAt', '>=', threshold)));
      triggerSnap.forEach(doc => {
        const uid = doc.ref.path.split('/')[1];
        const date = doc.data().createdAt?.toDate();
        if (date && (!tempMap[uid] || date > tempMap[uid])) tempMap[uid] = date;
      });
      setActivityMap(tempMap);
    } catch (error) { console.error("Activity fetch failed", error); }
  };

  useEffect(() => {
    fetchActivity();
    const unsubscribe = onSnapshot(collection(db, 'users'), async (snapshot) => {
      const userList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(u => u.role !== 'DEPENDENT');
      const fetchedUsers = await Promise.all(userList.map(async (u) => {
        const contactsSnap = await getDocs(collection(db, `users/${u.id}/contacts`));
        const allDocs = contactsSnap.docs.map(d => d.data());
        const actualTrustedBy = allDocs.filter(d => d.emergencyForThem === true && (d.forThemStatus === "ACCEPTED" || d.emergencyStatus === "ACCEPTED")).length;
        const actualMyEmergency = allDocs.filter(d => d.emergencyForMe === true && (d.forMeStatus === "ACCEPTED" || d.emergencyStatus === "ACCEPTED")).length;
        return {
          id: u.id,
          name: u.name || u.displayName || 'Unknown',
          email: u.email || 'No Email',
          phone: u.phone || u.phoneNumber || 'No Phone',
          role: u.role || 'USER', 
          joinDate: u.createdAt ? u.createdAt.toDate() : new Date(),
          dependentCount: u.dependentCount || 0,
          emergencyCount: actualTrustedBy, 
          myEmergencyCount: actualMyEmergency, 
          contactsCount: contactsSnap.size, // ✅ Total documents in contacts collection
          businessType: u.businessType || 'User',
          businessCategory: u.businessCategory || '',
          isDisabled: u.isDisabled || false,
          disabledReason: u.disabledReason || '', 
          gender: u.gender || 'Unknown',
          dob: u.dob ? (typeof u.dob.toDate === 'function' ? u.dob.toDate() : new Date(u.dob)) : null
        };
      }));
      setUsers(fetchedUsers);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let result = users;
    if (typeFilter !== 'All') result = result.filter(u => u.businessType === typeFilter);
    if (activityFilter === 'Active') result = result.filter(u => activityMap[u.id]);
    else if (activityFilter === 'Inactive') result = result.filter(u => !activityMap[u.id]);
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(u => u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower) || u.phone.includes(lower));
    }
    setFilteredUsers(result);
  }, [users, searchTerm, typeFilter, activityFilter, activityMap]);

  const analytics = useMemo(() => {
    const stats = {
      total: users.length, activeCount: 0, activePercentage: 0,
      types: [{ name: 'User', value: 0 }, { name: 'Business', value: 0 }],
      gender: [{ name: 'Male', value: 0 }, { name: 'Female', value: 0 }, { name: 'Unknown', value: 0 }],
      age: [{ name: '< 20', value: 0 }, { name: '21 - 35', value: 0 }, { name: '36 - 50', value: 0 }, { name: '51+', value: 0 }],
      countries: {} 
    };
    const currentYear = new Date().getFullYear();
    users.forEach(user => {
      if (activityMap[user.id]) stats.activeCount++;
      if (user.businessType === 'Business') stats.types[1].value++; else stats.types[0].value++;
      const g = (user.gender || 'Unknown').toLowerCase();
      if (g === 'male') stats.gender[0].value++; else if (g === 'female') stats.gender[1].value++; else stats.gender[2].value++;
      if (user.dob) {
        const age = currentYear - user.dob.getFullYear();
        if (age < 20) stats.age[0].value++; else if (age <= 35) stats.age[1].value++; else if (age <= 50) stats.age[2].value++; else stats.age[3].value++;
      }
      const country = getCountryFromPhone(user.phone);
      stats.countries[country] = (stats.countries[country] || 0) + 1;
    });
    stats.activePercentage = stats.total > 0 ? ((stats.activeCount / stats.total) * 100).toFixed(1) : 0;
    stats.types = stats.types.filter(i => i.value > 0);
    return stats;
  }, [users, activityMap]);

  // ✅ UPDATED: Export Logic with Contacts Count
  const exportToExcel = () => {
    const data = filteredUsers.map(u => ({
      "Name": u.name, "Email": u.email, "Phone": u.phone,
      "Account Type": u.businessType, "Category": u.businessCategory || 'N/A',
      "Status": u.isDisabled ? `Disabled (${u.disabledReason})` : "Active",
      "Deps": u.dependentCount, "Contacts": u.contactsCount, "Trusted By": u.emergencyCount, "My Emergency": u.myEmergencyCount 
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, `Ccuring_Directory_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const openDisableModal = (u) => u.isDisabled ? confirmEnable(u) : setDisableModal({ isOpen: true, user: u, reason: REASON_OPTIONS[0] });

  const confirmEnable = async (u) => {
    if (!window.confirm(`Enable ${u.name}?`)) return;
    await updateDoc(doc(db, 'users', u.id), { isDisabled: false, disabledReason: "" });
    await updateDoc(doc(db, 'profiles', u.id), { loginDisabled: false });
  };

  const handleDisableSubmit = async () => {
    const { user, reason } = disableModal;
    await updateDoc(doc(db, 'users', user.id), { isDisabled: true, disabledReason: reason, disabledAt: serverTimestamp() });
    await updateDoc(doc(db, 'profiles', user.id), { loginDisabled: true });
    setDisableModal({ isOpen: false, user: null, reason: '' });
  };

  const toggleBusinessType = async (user) => {
    const isUpgrading = user.businessType === 'User';
    let selectedCat = null; let fee = 0;
    if (isUpgrading) {
      const choice = window.prompt("Select Category:\n1. Transport (N5k)\n2. School (N30k)\n3. Hospital (N30k)\n4. Estate (N50k)");
      if (!BUSINESS_CATEGORIES[choice]) return alert("Cancelled.");
      selectedCat = BUSINESS_CATEGORIES[choice].name; fee = BUSINESS_CATEGORIES[choice].fee;
    }
    const batch = writeBatch(db);
    const newType = isUpgrading ? 'Business' : 'User';
    batch.update(doc(db, 'users', user.id), { businessType: newType, businessCategory: selectedCat || "" });
    batch.update(doc(db, 'profiles', user.id), { businessType: newType, businessCategory: selectedCat || "" });
    if (isUpgrading) {
        const invRef = doc(collection(db, `users/${user.id}/invoices`));
        batch.set(invRef, { amount: fee, status: "Paid", category: selectedCat, createdAt: serverTimestamp(), isBusinessInvoice: true });
    }
    await batch.commit();
    alert("Success.");
  };

  if (loading) return <div className="container">Loading Directory...</div>;

  return (
    <div className="container">
      <div className="header">
        <h1>User Management</h1>
        <button onClick={exportToExcel} className="export-btn">📊 Export Excel</button>
      </div>

      <div className="analytics-grid">
        <div className="stat-card">
          <div className="stat-title">Total Users</div>
          <div className="total-number">{analytics.total}</div>
          <div className="stat-label">
            <span style={{color: '#10b981', fontWeight: 'bold'}}>{analytics.activeCount}</span> Active ({analytics.activePercentage}%)
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-title">Account Split</div>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie data={analytics.types} innerRadius={40} outerRadius={60} dataKey="value" paddingAngle={5}>
                {analytics.types.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="filters">
        <input type="text" placeholder="Search name/email/phone..." className="search-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        <select className="type-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="All">All Types</option>
          <option value="User">Standard User</option>
          <option value="Business">Business</option>
        </select>
        <select className="type-select" value={activityFilter} onChange={e => setActivityFilter(e.target.value)}>
          <option value="All">All Activity</option>
          <option value="Active">Active (30d)</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>User Info</th>
              <th>Activity Status</th>
              <th>Counts</th>
              <th>Account Details</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} style={{ opacity: user.isDisabled ? 0.7 : 1, background: user.isDisabled ? '#fff1f2' : 'transparent' }}>
                <td>
                  <div className="user-name">{user.name}</div>
                  <div className="user-email" style={{fontSize: '11px', color: '#6366f1'}}>{user.email}</div>
                </td>
                <td>
                  {activityMap[user.id] ? <span className="badge badge-green">ACTIVE</span> : <span className="badge" style={{background: '#eee', color: '#999'}}>INACTIVE</span>}
                </td>
                <td>
                  {/* ✅ Added Contacts Count here */}
                  <div style={{fontSize:'12px'}}>Contacts: <b style={{color: '#4f46e5'}}>{user.contactsCount}</b></div>
                  <div style={{fontSize:'12px'}}>Deps: <b>{user.dependentCount}</b></div>
                  <div style={{fontSize:'12px'}}>Trusted By: <b>{user.emergencyCount}</b></div>
                  <div style={{fontSize:'12px'}}>My Emergency: <b>{user.myEmergencyCount}</b></div>
                </td>
                <td>
                   <span className={user.businessType === 'Business' ? "badge badge-purple" : "badge badge-green"}>{user.businessType}</span>
                   {user.businessCategory && <div style={{fontSize: '10px', marginTop: '4px', fontWeight: 'bold', color: '#6b21a8'}}>{user.businessCategory.toUpperCase()}</div>}
                </td>
                <td>
                  <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
                    <button onClick={() => openDisableModal(user)} className={user.isDisabled ? "action-btn btn-success" : "action-btn btn-danger"}>
                      {user.isDisabled ? '✅ Enable' : '🚫 Disable'}
                    </button>
                    <button onClick={() => toggleBusinessType(user)} className="action-btn btn-primary" style={{fontSize:'11px'}}>Change Type</button>
                    {user.isDisabled && user.disabledReason && (
                      <div style={{fontSize: '10px', color: '#be123c', fontWeight: 'bold', maxWidth: '120px', lineHeight: '1.2'}}>REASON: {user.disabledReason}</div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {disableModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{maxWidth: '400px'}}>
            <h3>Disable Account</h3>
            <p>Reason for disabling <b>{disableModal.user?.name}</b>:</p>
            <select className="modal-select" value={disableModal.reason} onChange={(e) => setDisableModal({ ...disableModal, reason: e.target.value })} style={{width: '100%', marginBottom: '20px'}}>
              {REASON_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <div className="modal-actions">
              <button onClick={() => setDisableModal({ isOpen: false, user: null, reason: '' })} className="cancel-btn">Cancel</button>
              <button onClick={handleDisableSubmit} className="save-btn" style={{background: '#ef4444'}}>Confirm Disable</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;